import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "timeout" | "hydrating";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  status: AuthStatus;
  profile: { full_name?: string; onboarding_completed?: boolean } | null;
  profileLoaded: boolean;
  signOut: () => Promise<void>;
  isHydrated: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Removed timeout/delay constants - they caused instability

// Check if we're in Safari Private Mode (localStorage might be blocked)
const checkStorageAvailable = (): boolean => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<{ full_name?: string; onboarding_completed?: boolean } | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [isHydrated, setIsHydrated] = useState(false);
  
  // Track if initial session check completed to avoid premature unauthenticated state
  const initialCheckDone = useRef(false);
  const hasEverBeenAuthenticated = useRef(false);
  const profileTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Use refs to track current state for timeout checks (avoids stale closures)
  const statusRef = useRef(status);
  const isHydratedRef = useRef(isHydrated);
  
  // Keep refs in sync with state
  useEffect(() => {
    statusRef.current = status;
    isHydratedRef.current = isHydrated;
  }, [status, isHydrated]);

  useEffect(() => {
    let mounted = true;

    const storageAvailable = checkStorageAvailable();
    console.log("[AuthContext] Initializing...", { storageAvailable });

    // Fetch profile helper with safety timeout
    const fetchProfile = async (userId: string) => {
      // Clear any existing timeout
      if (profileTimeoutRef.current) {
        clearTimeout(profileTimeoutRef.current);
      }

      // Safety timeout: force profileLoaded after 5 seconds to prevent UI blocking
      profileTimeoutRef.current = setTimeout(() => {
        if (mounted) {
          console.warn("[AuthContext] Profile fetch timeout - forcing profileLoaded");
          setProfileLoaded(true);
        }
      }, 5000);

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("full_name, onboarding_completed")
          .eq("id", userId)
          .single();

        if (mounted) {
          if (profileTimeoutRef.current) {
            clearTimeout(profileTimeoutRef.current);
          }
          if (!error && data) {
            setProfile(data);
          }
          setProfileLoaded(true);
        }
      } catch (e) {
        console.error("[AuthContext] Profile fetch error", e);
        if (mounted) {
          if (profileTimeoutRef.current) {
            clearTimeout(profileTimeoutRef.current);
          }
          setProfileLoaded(true);
        }
      }
    };

    // Global safety timeout: prevent infinite loading state (15 seconds)
    // Only triggers if still in loading state AND not hydrated - uses refs to avoid stale closures
    const globalTimeout = setTimeout(() => {
      if (mounted && !initialCheckDone.current) {
        console.warn("[AuthContext] Global timeout - forcing state resolution");
        initialCheckDone.current = true;
        setStatus("unauthenticated");
        setIsHydrated(true);
        setProfileLoaded(true);
      }
    }, 15000);

    // Listen to auth state changes FIRST (as per Supabase best practices)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;

      console.log("[AuthContext] onAuthStateChange:", event, !!nextSession);

      // Synchronous state updates only
      if (nextSession?.user) {
        setSession(nextSession);
        setUser(nextSession.user);
        setStatus("authenticated");
        setIsHydrated(true);
        hasEverBeenAuthenticated.current = true;
        initialCheckDone.current = true;
        
        // Defer profile fetch with setTimeout to avoid deadlock
        setTimeout(() => {
          if (mounted) fetchProfile(nextSession.user.id);
        }, 0);
      } else if (event === 'SIGNED_OUT' || initialCheckDone.current) {
        // Only clear if explicitly signed out OR we've done initial check
        setSession(null);
        setUser(null);
        setProfile(null);
        setProfileLoaded(true);
        setStatus("unauthenticated");
        setIsHydrated(true);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: initialSession }, error }) => {
      if (!mounted) return;
      
      initialCheckDone.current = true;

      console.log("[AuthContext] getSession:", !!initialSession, error?.message);

      if (initialSession?.user) {
        hasEverBeenAuthenticated.current = true;
        setSession(initialSession);
        setUser(initialSession.user);
        setStatus("authenticated");
        setIsHydrated(true);
        
        setTimeout(() => {
          if (mounted) fetchProfile(initialSession.user.id);
        }, 0);
      } else {
        setStatus("unauthenticated");
        setIsHydrated(true);
      }
    }).catch((err) => {
      console.error("[AuthContext] getSession error:", err);
      if (mounted) {
        initialCheckDone.current = true;
        setStatus("unauthenticated");
        setIsHydrated(true);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(globalTimeout);
      if (profileTimeoutRef.current) {
        clearTimeout(profileTimeoutRef.current);
      }
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    console.log("[AuthContext] Signing out...");
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setProfileLoaded(false);
    setStatus("unauthenticated");
    setIsHydrated(true);
  };

  return (
    <AuthContext.Provider value={{ user, session, status, profile, profileLoaded, signOut, isHydrated }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
